import React, { useState, useRef, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { FiSearch, FiChevronDown, FiCheck, FiUsers } from 'react-icons/fi';

// Selector de colaborador con foto y buscador.
// Reemplaza a los <select> nativos, que no admiten imágenes ni búsqueda y se
// vuelven inmanejables cuando la lista pasa de una docena de personas.
const Avatar = ({ user, size = 28 }) => (
    user?.photoURL ? (
        <img src={user.photoURL} alt="" className="rounded-full object-cover shrink-0" style={{ width: size, height: size }} />
    ) : (
        <div className="rounded-full bg-gradient-to-br from-sky-500 to-blue-700 text-white font-bold flex items-center justify-center shrink-0"
            style={{ width: size, height: size, fontSize: size * 0.4 }}>
            {(user?.displayName || user?.email || '?').charAt(0).toUpperCase()}
        </div>
    )
);

const UserPicker = ({
    users = [],
    value,
    onChange,
    placeholder = 'Seleccionar...',
    allowAll = false,
    allLabel = 'Todos',
    allValue = 'ALL',
    className = '',
    buttonClassName = '',
    disabled,
}) => {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const [pos, setPos] = useState(null);
    const btnRef = useRef(null);
    const popRef = useRef(null);
    const searchRef = useRef(null);

    useEffect(() => {
        if (!open) return;
        const onClickOutside = (e) => {
            if (popRef.current?.contains(e.target) || btnRef.current?.contains(e.target)) return;
            setOpen(false);
        };
        const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
        document.addEventListener('mousedown', onClickOutside);
        document.addEventListener('keydown', onKey);
        setTimeout(() => searchRef.current?.focus(), 30);
        return () => {
            document.removeEventListener('mousedown', onClickOutside);
            document.removeEventListener('keydown', onKey);
        };
    }, [open]);

    const selected = users.find(u => u.id === value);
    const isAll = allowAll && value === allValue;

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        const sorted = [...users].sort((a, b) => (a.displayName || a.email || '').localeCompare(b.displayName || b.email || ''));
        if (!q) return sorted;
        return sorted.filter(u => `${u.displayName || ''} ${u.email || ''}`.toLowerCase().includes(q));
    }, [users, query]);

    const toggle = () => {
        if (disabled) return;
        if (!open && btnRef.current) {
            const r = btnRef.current.getBoundingClientRect();
            setPos({ top: r.bottom + 4, left: r.left, width: Math.max(r.width, 260) });
        }
        setQuery('');
        setOpen(v => !v);
    };

    const pick = (id) => { onChange(id); setOpen(false); };

    return (
        <div className={className}>
            <button ref={btnRef} type="button" onClick={toggle} disabled={disabled}
                className={`w-full flex items-center gap-2 text-left transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${buttonClassName}`}>
                {isAll ? (
                    <>
                        <span className="w-7 h-7 rounded-full bg-gray-100 text-gray-500 flex items-center justify-center shrink-0"><FiUsers size={13} /></span>
                        <span className="flex-1 min-w-0 truncate font-semibold text-gray-700">{allLabel}</span>
                    </>
                ) : selected ? (
                    <>
                        <Avatar user={selected} />
                        <span className="flex-1 min-w-0 truncate font-semibold text-gray-800">{selected.displayName || selected.email}</span>
                    </>
                ) : (
                    <>
                        <span className="w-7 h-7 rounded-full bg-gray-100 shrink-0" />
                        <span className="flex-1 min-w-0 truncate text-gray-400">{placeholder}</span>
                    </>
                )}
                <FiChevronDown size={14} className={`text-gray-400 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
            </button>

            {open && pos && createPortal(
                <div ref={popRef} style={{ top: pos.top, left: pos.left, width: pos.width, zIndex: 90 }}
                    className="fixed bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden animate-in fade-in zoom-in-95 duration-100">
                    <div className="p-2.5 border-b border-gray-100 bg-gray-50/70">
                        <div className="relative">
                            <FiSearch size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                            <input ref={searchRef} value={query} onChange={e => setQuery(e.target.value)}
                                placeholder="Buscar colaborador..."
                                className="w-full pl-8 pr-2.5 py-2 text-[13px] bg-white border border-gray-200 rounded-xl outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-100" />
                        </div>
                    </div>
                    <div className="max-h-72 overflow-y-auto py-1">
                        {allowAll && !query && (
                            <button type="button" onClick={() => pick(allValue)}
                                className="w-full px-3 py-2 flex items-center gap-2.5 hover:bg-gray-50 transition-colors">
                                <span className="w-7 h-7 rounded-full bg-gray-100 text-gray-500 flex items-center justify-center shrink-0"><FiUsers size={13} /></span>
                                <span className="flex-1 text-left text-[13px] font-semibold text-gray-700">{allLabel}</span>
                                {isAll && <FiCheck size={14} className="text-sky-600 shrink-0" />}
                            </button>
                        )}
                        {filtered.map(u => (
                            <button key={u.id} type="button" onClick={() => pick(u.id)}
                                className={`w-full px-3 py-2 flex items-center gap-2.5 transition-colors ${u.id === value ? 'bg-sky-50' : 'hover:bg-gray-50'}`}>
                                <Avatar user={u} />
                                <span className="flex-1 min-w-0 text-left">
                                    <span className="block text-[13px] font-semibold text-gray-800 truncate">{u.displayName || u.email?.split('@')[0]}</span>
                                    <span className="block text-[10.5px] text-gray-400 truncate">{u.email}</span>
                                </span>
                                {u.id === value && <FiCheck size={14} className="text-sky-600 shrink-0" />}
                            </button>
                        ))}
                        {filtered.length === 0 && (
                            <p className="px-3 py-6 text-center text-[12px] text-gray-400">Sin resultados para "{query}"</p>
                        )}
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

export default UserPicker;
